// The manual/backdated service-record form (Session AR Item 11). Primarily for entering visits
// from before V2 went live; it writes a COMPLETED Job + JobSheet via POST /api/jobsheets/manual
// (no email, no invoice, no PDF -- history only; a service visit advances the lift's
// last-serviced anchor monotonically, a call-out never does).
//
// Session ACG: extracted verbatim from JobSheetsPage.tsx so the dashboard's "+ Add a service"
// quick action can reuse the exact same modal + endpoint without pulling the whole Service
// Records page (JobSheetList etc.) into the dashboard chunk.
import { useState } from "react";
import { Button } from "../../components/Button";
import { MODAL_ACTION_BTN, MODAL_ACTIONS, ModalShell } from "../../components/ModalShell";
import { JobPhotoUploader } from "./JobPhotoUploader";
import { ApiError, api } from "../../lib/api";
import { RecordPicker } from "../../components/RecordPicker";

// Local (not UTC) YYYY-MM-DD, so the client-side future-date guard matches the visitor's own
// calendar day right up to midnight (mirrors BackdatedCompletionModal's toYmd).
function toYmd(d: Date): string {
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function AddServiceRecordModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [assetId, setAssetId] = useState("");
  const [visitDate, setVisitDate] = useState("");
  const [visitType, setVisitType] = useState<"service" | "callout">("service");
  const [engineerName, setEngineerName] = useState("");
  const [outcome, setOutcome] = useState<"pass" | "fail" | "observations">("pass");
  const [notes, setNotes] = useState("");
  const [defectNotes, setDefectNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<{ visitType: string; jobId: string } | null>(null);

  async function save() {
    if (!assetId || !visitDate) {
      setError("Choose a lift and a visit date.");
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // The date input is a plain YYYY-MM-DD; send it as midday UTC so the recorded date is stable.
      const iso = new Date(`${visitDate}T12:00:00.000Z`).toISOString();
      const res = await api.jobsheets.createManual({
        assetId,
        visitDate: iso,
        visitType,
        engineerName: engineerName.trim() || undefined,
        outcome,
        notes: notes.trim() || undefined,
        defectNotes: defectNotes.trim() || undefined,
      });
      setDone({ visitType: res.visitType, jobId: res.jobId });
      onCreated();
    } catch (err) {
      setError(err instanceof ApiError ? err.message || err.code : "Could not save the record. Please try again.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <ModalShell ariaLabel="Add service record" onClose={() => !saving && onClose()} closeOnBackdrop width="max-w-lg" panelClassName="max-h-[90vh] overflow-y-auto p-6">
        <div className="mb-3 flex items-start justify-between gap-3">
          <h2 className="font-heading text-xl text-ele-blue">Add service record</h2>
          <button onClick={onClose} aria-label="Close" className="text-ele-body hover:text-ele-green">✕</button>
        </div>

        {done ? (
          <>
            <p role="status" className="rounded-ele border border-ele-green bg-ele-green/10 px-3 py-3 text-sm text-ele-green">
              {done.visitType === "callout"
                ? "Call-out record saved. It appears on the Call-outs page in the completed records section."
                : "Service record saved. It now appears in the list, on the lift record and in the customer portal."}
            </p>
            <div className="mt-4">
              <h3 className="mb-1 font-heading text-sm uppercase tracking-wide text-ele-blue">Add photos (optional)</h3>
              <p className="mb-1 text-xs text-ele-body opacity-70">Attach any site photos to this record now, or add them later from the record.</p>
              <JobPhotoUploader jobId={done.jobId} label="" />
            </div>
            <div className="mt-4 flex justify-end">
              <Button onClick={onClose}>Done</Button>
            </div>
          </>
        ) : (
          <>
            <p className="mb-3 text-sm text-ele-body opacity-70">
              For backdating a visit that was serviced before V2 went live. This does not email the customer, raise an invoice or generate a PDF; it
              records the history only.
            </p>
            {/* Session ASM: this form needs a lift, so it was the workaround for a call-out with no lift or customer on
                the register. That now has its own proper path, which also raises the draft invoice. */}
            <p className="mb-3 text-sm text-ele-body opacity-70">
              A call-out where the caller or the lift is not on the platform? Use Call-outs, then &ldquo;New call-out report&rdquo;,
              and choose &ldquo;Not in the platform?&rdquo;. No lift is needed, and it raises the draft invoice.
            </p>

            <div className="mb-3">
              <RecordPicker kind="lift" id="msr-lift" label="Lift" value={assetId} placeholder="Search by ELE number, site or serial" onChange={(id) => setAssetId(id)} />
            </div>

            <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm" htmlFor="msr-date">Date of visit</label>
                <input
                  id="msr-date"
                  type="date"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                  max={toYmd(new Date())}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm" htmlFor="msr-type">Visit type</label>
                <select
                  id="msr-type"
                  value={visitType}
                  onChange={(e) => setVisitType(e.target.value as "service" | "callout")}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                >
                  <option value="service">Service (PPM)</option>
                  <option value="callout">Call-out</option>
                </select>
              </div>
            </div>

            <div className="mb-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
              <div>
                <label className="mb-1 block text-sm" htmlFor="msr-eng">Engineer</label>
                <input
                  id="msr-eng"
                  type="text"
                  value={engineerName}
                  onChange={(e) => setEngineerName(e.target.value)}
                  placeholder="Name (optional)"
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                />
              </div>
              <div>
                <label className="mb-1 block text-sm" htmlFor="msr-outcome">Outcome</label>
                <select
                  id="msr-outcome"
                  value={outcome}
                  onChange={(e) => setOutcome(e.target.value as "pass" | "fail" | "observations")}
                  className="w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
                >
                  <option value="pass">Pass</option>
                  <option value="fail">Fail</option>
                  <option value="observations">Observations noted</option>
                </select>
              </div>
            </div>

            <label className="mb-1 block text-sm" htmlFor="msr-notes">Notes</label>
            <textarea
              id="msr-notes"
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />

            <label className="mb-1 block text-sm" htmlFor="msr-defect">Defect notes (optional)</label>
            <textarea
              id="msr-defect"
              rows={2}
              value={defectNotes}
              onChange={(e) => setDefectNotes(e.target.value)}
              className="mb-3 w-full rounded-ele border border-ele-purple-accent bg-ele-field px-3 py-2 text-sm outline-none focus:border-ele-green"
            />

            {error && <p role="alert" className="mb-3 rounded-ele border border-ele-danger bg-ele-surface px-3 py-2 text-sm text-ele-strong">{error}</p>}

            <div className={`gap-3 ${MODAL_ACTIONS}`}>
              <button onClick={onClose} disabled={saving} className={`rounded-ele border border-ele-purple-accent px-4 py-2 text-sm disabled:opacity-50 ${MODAL_ACTION_BTN}`}>Cancel</button>
              <Button onClick={save} disabled={saving || !assetId || !visitDate} className={MODAL_ACTION_BTN}>
                {saving ? "Saving..." : "Save record"}
              </Button>
            </div>
          </>
        )}
    </ModalShell>
  );
}
